import React, { useState, useContext } from "react";
import Filter from "./Filter";
import Content from "../common/Content";
import styled from "styled-components";
import Header from "./Header";
import { colors, spacing } from "../theme";
import { ApiResult } from "../../lib/CmsApi";
import AccordionSection from "./AccordionSection";
import Label from "../common/Label";
import GlobalContext from "../context/GlobalContext";
import CustomPageHead from "../common/CustomPageHead";
import { Tabs, TabPanels, TabPanel } from "@reach/tabs";
import useDidUpdateEffect from "../../lib/useDidUpdateEffect";
import { Router } from "../../lib/routes";

const Container = styled.div`
  background: ${colors.anotherblue};
  ${spacing("padding-bottom", 160)}
`;
const FilteredContacts = styled(Content)`
  ${spacing("padding-top", 80)}
  ${spacing("margin-bottom", 80)}
`;
export type Props = ApiResult<"loadContacts"> & {
  activeTab?: string;
  segmentContacts: {
    [segment: string]: {
      [cluster: string]: {
        name: string;
        email: string;
        phone: string;
        type: string;
        role: string;
      }[];
    };
  };
};
export default function ContactsPage(props: Props) {
  const segments = Object.keys(props.segmentContacts);
  const { segments: globalSegments } = useContext(GlobalContext);

  const orderedSegments = globalSegments.items.filter(s =>
    segments.includes(s.name)
  );

  const initialTab = props.activeTab || orderedSegments[0].slug;
  const [activeTab, setActiveTab] = useState(initialTab);

  const handleTabChange = index => {
    const type = orderedSegments[index];
    if (!type) return;

    setActiveTab(type.slug);
    const { tab, ...query } = Router.query;
    if (tab === type.slug) return;

    Router.replaceRoute(
      Router.route.replace("/", ""),
      {
        ...query,
        tab: type.slug
      },
      { shallow: true }
    );
  };
  if (!segments) return null;
  const activeIndex = orderedSegments.findIndex(t => t.slug === activeTab);
  return (
    <Container>
      <CustomPageHead title="Kontakt" desc={props.intro} seo={props.seo} />
      <Content padded>
        <Header headline={props.headline} intro={props.intro} />
      </Content>
      {props.segmentContacts && orderedSegments && (
        <>
          <main>
            <Tabs onChange={handleTabChange} index={activeIndex}>
              <Filter
                options={orderedSegments.map(s => s.name)}
                label="Sie sind"
                activeIndex={activeIndex}
              />
              <TabPanels>
                {orderedSegments.map((segment, idx) => (
                  <TabPanel key={segment.slug}>
                    <FilteredContacts padded>
                      {Object.entries(props.segmentContacts[segment.name]).map(
                        ([cluster, contacts], jdx) => (
                          <AccordionSection
                            uuid={`c_${idx}_${jdx}`}
                            key={cluster}
                            label={segment.name}
                            headline={cluster}
                            contacts={contacts.filter(c => !!c.name)}
                          />
                        )
                      )}
                    </FilteredContacts>
                  </TabPanel>
                ))}
              </TabPanels>
            </Tabs>
          </main>
          <aside aria-label="Weitere Kontakte">
            <Content padded smallTopMargin>
              <Label tiny>Weitere Kontakte</Label>
              <div>
                {props.contactSections &&
                  props.contactSections
                    .map((section, idx) =>
                      section.contacts.length ? (
                        <AccordionSection
                          uuid={`mc_${idx}`}
                          key={section.name}
                          headline={section.name}
                          contacts={section.contacts
                            .filter(sc => !!sc.contact.name)
                            .map(({ role, contact }) => ({
                              role,
                              ...contact
                            }))}
                        />
                      ) : null
                    )
                    .filter(Boolean)}
              </div>
            </Content>
          </aside>
        </>
      )}
    </Container>
  );
}
